import {
	Badge,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardTitle,
	Skeleton,
	Switch,
} from "@ancatag/at-design";
import {
	Activity,
	AlertCircle,
	ArrowLeft,
	Clock,
	MessageSquare,
	TrendingUp,
	Zap,
} from "lucide-react";
import { useState } from "react";
import { Link, useRoute } from "wouter";
import { CreateProjectDialog } from "../components/projects/CreateProjectDialog";
import { ProjectEmptyState } from "../components/projects/ProjectEmptyState";
import { useAuth } from "../context/AuthContext";
import { useProjectContext } from "../context/ProjectContext";
import {
	useApiKey,
	useApiKeyMetrics,
	useToggleApiKey,
} from "../hooks/useApiKeys";

export function ApiKeyDetail() {
	const [, params] = useRoute("/api-keys/:keyId");
	const keyId = params?.keyId || "";
	const {
		selectedProject,
		isLoading: projectLoading,
		refreshProjects,
	} = useProjectContext();
	const [createProjectOpen, setCreateProjectOpen] = useState(false);

	const { data: apiKey, isLoading, isError } = useApiKey(keyId);
	const { data: metrics } = useApiKeyMetrics(keyId, "day");
	const toggleKey = useToggleApiKey();

	const handleToggleActive = async () => {
		if (apiKey) {
			await toggleKey.mutateAsync({
				keyId: apiKey.id,
				isActive: !apiKey.isActive,
			});
		}
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "Never";
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	if (projectLoading) {
		return <ApiKeyDetailSkeleton />;
	}

	const { user } = useAuth();
	const isSuperAdmin = user?.role === "superAdmin";

	if (!selectedProject && !isSuperAdmin) {
		return (
			<div className="space-y-6">
				<Link href="/api-keys">
					<Button variant="ghost" className="gap-2">
						<ArrowLeft className="w-4 h-4" />
						Back to API Keys
					</Button>
				</Link>
				<ProjectEmptyState
					onCreateProject={() => setCreateProjectOpen(true)}
					onRefresh={refreshProjects}
				/>
				<CreateProjectDialog
					open={createProjectOpen}
					onOpenChange={setCreateProjectOpen}
				/>
			</div>
		);
	}

	if (isLoading) {
		return <ApiKeyDetailSkeleton />;
	}

	if (isError || !apiKey) {
		return (
			<div className="space-y-6">
				<Link href="/api-keys">
					<Button variant="ghost" className="gap-2">
						<ArrowLeft className="w-4 h-4" />
						Back to API Keys
					</Button>
				</Link>
				<Card className="border-destructive/50 bg-destructive/10">
					<CardContent className="pt-6">
						<div className="flex items-center gap-2 text-destructive">
							<AlertCircle className="w-5 h-5" />
							<p>Failed to load API key details</p>
						</div>
					</CardContent>
				</Card>
			</div>
		);
	}

	const totalRequests = metrics?.summary.totalRequests || 0;
	const successRate = totalRequests
		? ((totalRequests - (metrics?.summary.errors || 0)) / totalRequests) * 100
		: 0;
	const cacheHitRate = metrics?.summary.cacheHitRate || 0;
	const avgLatency = metrics?.summary.avgLatencyMs || 0;
	const tokensSaved = metrics?.summary.tokensSaved || 0;

	return (
		<div className="space-y-6">
			{/* Header */}
			<div className="flex items-start justify-between">
				<div className="space-y-1">
					<Link href="/api-keys">
						<Button variant="ghost" size="sm" className="gap-2 -ml-2 mb-2">
							<ArrowLeft className="w-4 h-4" />
							Back to API Keys
						</Button>
					</Link>
					<h1 className="text-3xl font-bold text-foreground">{apiKey.name}</h1>
					{apiKey.description && (
						<p className="text-muted-foreground">{apiKey.description}</p>
					)}
				</div>
				<div className="flex items-center gap-4">
					<div className="flex items-center gap-2">
						<span className="text-sm text-muted-foreground">Status:</span>
						<Switch
							checked={apiKey.isActive}
							onCheckedChange={handleToggleActive}
						/>
						<span
							className={`text-sm font-medium ${
								apiKey.isActive ? "text-green-600" : "text-muted-foreground"
							}`}
						>
							{apiKey.isActive ? "Active" : "Inactive"}
						</span>
					</div>
				</div>
			</div>

			{/* Key Info Card */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Key Information</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
						<div className="md:col-span-2 lg:col-span-4">
							<p className="text-sm text-muted-foreground mb-2">
								API Key Prefix
							</p>
							<div className="flex items-center gap-3 p-3 bg-muted/50 border border-border rounded-lg">
								<code className="text-sm font-mono text-foreground select-all break-all flex-1">
									{apiKey.keyPreview || apiKey.keyPrefix || "No key available"}
								</code>
							</div>
							<p className="text-xs text-muted-foreground mt-2">
								This is a prefix identifier. The full key is only shown once
								when created.
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">Created</p>
							<p className="text-sm font-medium">
								{formatDate(apiKey.createdAt)}
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">Last Used</p>
							<p className="text-sm font-medium">
								{formatDate(apiKey.lastUsedAt)}
							</p>
						</div>
						<div>
							<p className="text-sm text-muted-foreground mb-1">Expires</p>
							<p className="text-sm font-medium">
								{apiKey.expiresAt ? formatDate(apiKey.expiresAt) : "Never"}
							</p>
						</div>
					</div>

					{/* Permissions */}
					<div className="mt-6 pt-6 border-t">
						<p className="text-sm text-muted-foreground mb-3">Permissions</p>
						<div className="flex flex-wrap gap-2">
							{apiKey.allowedGenerationTypes?.map((type) => (
								<Badge key={type} variant="secondary" className="gap-1">
									<MessageSquare className="w-3 h-3" />
									{type.charAt(0).toUpperCase() + type.slice(1)}
								</Badge>
							)) || (
								<Badge variant="secondary" className="bg-gray-500/10">
									All Generation Types
								</Badge>
							)}
						</div>
					</div>
				</CardContent>
			</Card>

			{/* Usage Stats */}
			<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Zap className="w-4 h-4" />
							Total Requests
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">
							{(totalRequests || 0).toLocaleString()}
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Activity className="w-4 h-4" />
							Success Rate
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold text-green-600">
							{successRate.toFixed(1)}%
						</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<Clock className="w-4 h-4" />
							Avg Response Time
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{avgLatency.toFixed(0)}ms</div>
					</CardContent>
				</Card>

				<Card>
					<CardHeader className="pb-2">
						<CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
							<TrendingUp className="w-4 h-4" />
							Cache Hit Rate
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className="text-2xl font-bold">{cacheHitRate.toFixed(1)}%</div>
					</CardContent>
				</Card>
			</div>

			{/* Cache & savings */}
			<Card>
				<CardHeader>
					<CardTitle className="text-lg">Cache & Savings</CardTitle>
				</CardHeader>
				<CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
					<div>
						<p className="text-sm text-muted-foreground mb-1">Cache Hit Rate</p>
						<p className="text-2xl font-bold">{cacheHitRate.toFixed(1)}%</p>
					</div>
					<div>
						<p className="text-sm text-muted-foreground mb-1">Tokens Used</p>
						<p className="text-2xl font-bold">
							{metrics?.summary.tokensUsed?.toLocaleString() || 0}
						</p>
					</div>
					<div>
						<p className="text-sm text-muted-foreground mb-1">Tokens Saved</p>
						<p className="text-2xl font-bold text-green-600">
							{tokensSaved.toLocaleString()}
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

function ApiKeyDetailSkeleton() {
	return (
		<div className="space-y-6">
			<div>
				<Skeleton className="h-8 w-24 mb-4" />
				<Skeleton className="h-10 w-64 mb-2" />
				<Skeleton className="h-4 w-96" />
			</div>
			<Card>
				<CardHeader>
					<Skeleton className="h-6 w-32" />
				</CardHeader>
				<CardContent>
					<div className="grid grid-cols-4 gap-6">
						{[1, 2, 3, 4].map((i) => (
							<div key={i}>
								<Skeleton className="h-4 w-20 mb-2" />
								<Skeleton className="h-6 w-32" />
							</div>
						))}
					</div>
				</CardContent>
			</Card>
			<div className="grid grid-cols-4 gap-4">
				{[1, 2, 3, 4].map((i) => (
					<Card key={i}>
						<CardHeader className="pb-2">
							<Skeleton className="h-4 w-24" />
						</CardHeader>
						<CardContent>
							<Skeleton className="h-8 w-16" />
						</CardContent>
					</Card>
				))}
			</div>
		</div>
	);
}
